import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDaily, useScreenShare as useDailyScreenShare } from '@daily-co/daily-react-hooks';
import PropTypes from 'prop-types';

export const MAX_SCREEN_SHARES = 2;

const ScreenShareContext = createContext(null);

export const ScreenShareProvider = ({ children }) => {
  const callObject = useDaily();
  // A stream we captured ourselves (shareCurrentTab) and handed to Daily: Daily only stops it when
  // the browser ends the share, so every other stop goes through stopOwnStream.
  const ownStreamRef = useRef(null);

  const stopOwnStream = useCallback(() => {
    const stream = ownStreamRef.current;
    if (!stream) return;
    ownStreamRef.current = null;
    stream.getTracks().forEach((t) => t.stop());
  }, []);

  const {
    isSharingScreen,
    screens,
    startScreenShare: startDailyScreenShare,
    stopScreenShare: stopDailyScreenShare,
  } = useDailyScreenShare({ onLocalScreenShareStopped: stopOwnStream });

  const stopScreenShare = useCallback(() => {
    stopDailyScreenShare();
    stopOwnStream();
  }, [stopDailyScreenShare, stopOwnStream]);

  // the browser's own picker; an own stream still around would be orphaned by the new share
  const startScreenShare = useCallback(() => {
    stopOwnStream();
    startDailyScreenShare();
  }, [startDailyScreenShare, stopOwnStream]);

  // Chrome offers "share this tab" as a one-click dialog; other browsers ignore these hints and show
  // their usual picker. Must be called from a user gesture with no await before getDisplayMedia.
  const shareCurrentTab = useCallback(async () => {
    if (!callObject) return;
    stopOwnStream();
    if (isSharingScreen) stopDailyScreenShare();
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      });
    } catch (e) {
      return; // picker dismissed
    }
    const track = stream.getVideoTracks()[0];
    if (!track || track.readyState === 'ended') return;
    ownStreamRef.current = stream;
    // the browser's "Stop sharing" bar ends the track; track.stop() from our side doesn't fire this
    track.addEventListener('ended', stopScreenShare, { once: true });
    callObject.startScreenShare({ mediaStream: stream });
  }, [callObject, isSharingScreen, stopDailyScreenShare, stopOwnStream, stopScreenShare]);

  const isDisabled = useMemo(() => screens.length >= MAX_SCREEN_SHARES && !isSharingScreen,
    [isSharingScreen, screens.length]
  );

  useEffect(() => {
    if (!callObject) return;
    const handleTrackStarted = (ev) => { // if i share & new share started, stop mine
      if (ev.type === 'screenVideo'
        && !ev.participant?.local
        && callObject.participants().local?.screen
      ) {
        stopScreenShare();
      }
    };
    // startScreenShare({ mediaStream }) doesn't throw: a failed start is reported here
    const handleNonfatal = (ev) => {
      if (ev?.type === 'screen-share-error') stopOwnStream();
    };
    callObject.on('track-started', handleTrackStarted);
    callObject.on('left-meeting', stopOwnStream);
    callObject.on('nonfatal-error', handleNonfatal);
    return () => {
      callObject.off('track-started', handleTrackStarted);
      callObject.off('left-meeting', stopOwnStream);
      callObject.off('nonfatal-error', handleNonfatal);
      stopOwnStream(); // the call object is replaced or destroyed (error paths skip left-meeting)
    };
  }, [callObject, stopScreenShare, stopOwnStream]);

  return (
    <ScreenShareContext.Provider
      value={{
        isSharingScreen,
        isDisabled,
        screens,
        startScreenShare,
        stopScreenShare,
        shareCurrentTab,
      }}
    >
      {children}
    </ScreenShareContext.Provider>
  );
};

ScreenShareProvider.propTypes = {
  children: PropTypes.node,
};

export const useScreenShare = () => useContext(ScreenShareContext);
